import React, { Fragment } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
// use a special type of SidePanel instead of the basic one
import { TRouteSidePanel } from './TRouteSidePanel'
import { TRouteBottomPanel } from './TRouteBottomPanel'
// we import and bind these because they're long and complex enough to deserve
// their own file, imports, etc, but it's very convenient to access this.props
// directly instead of booting up a component
import { renderSelectedFeature, renderHoveredFeature } from './mapRendering'
import { EmptyPlaceholder } from '../EmptyPlaceholder'
import { showGallery } from 'features/gallery/showGallery'
import { Loadable } from 'common/Loadable'
import { MapV2 } from 'common/MapV2'
import { TrackSnapPoint } from 'common/MapV2/TrackSnapPoint'
import { FullscreenButton } from 'common/MapV2/controls/FullscreenButton'
import { SettingsMenu } from 'common/MapV2/controls/SettingsMenu'
import { Topo } from 'common/styleguide/Topo'
import { PhotoClusters } from 'common/Maplet/PhotoClusters'
import { POIv3 } from 'common/Map/POI'
import { segmentPopoverClearance } from 'common/Map/mapUtils'
import { TrackLine } from 'common/MapV2/TrackLine'
import { DroppablePage } from 'common/DroppablePage'
import { MetricUnitsContext, withMetricUnits } from 'common/units/MetricUnitsContext'
import { SHARE_MODAL } from 'common/modals/ShareModal'
import { TROUTE_INFO_MODAL } from 'common/modals/TRouteInfoModal'
import { FLAG_USER_AS_IMPORTANT_DIALOG, TROUTE_DELETE_PHOTOS_MODAL } from 'common/dialogs/AdminActionDialogs'
import { ExportSvgMenuItem } from 'common/GlobalNav/AdminMenu/Items'
import { ThreePanelLayout, MainPanel, BottomPanel } from 'common/layouts/ThreePanelLayout'
import { AdminMenuItem, AdminMenuSeparator } from 'common/GlobalNav/AdminMenu'
import { featureTypes } from 'common/MapV2/featureTypes'
import { apiv3 } from 'lib/apiv3'
import { softError } from 'lib/softError'
import fetchJSON from 'lib/fetchJSON'
import { getPhotoUploadMaxConcurrency, queryStringSet } from 'lib/utils'
import * as userActions from 'lib/api/currentUser'
import { showModal } from 'lib/modalUtils'
import { urls } from 'lib/urls'
import { tileServices } from 'lib/tileServices'
import { initialViewportForTRoute } from 'lib/geoUtils'
import { fm, commonUnits } from 'lib/localization'
import { tripActions, tripSelectors } from 'data/resources/trips'
import { routeActions, routeSelectors } from 'data/resources/routes'
import { photoActions, photoSelectors } from 'data/resources/photos'
import { ambassadorRouteSelectors } from 'data/resources/ambassadorRoutes'
import { pointOfInterestSelectors } from 'data/resources/pointsOfInterest'
import { TrackLocation, Viewport } from 'data/records'
import _ from 'lodash'
import { Record, OrderedMap } from 'immutable'
import PromisePool from 'es6-promise-pool'
import { injectIntl } from 'react-intl'
import queryString from 'query-string'

const MetricAwareTRouteBottomPanel = withMetricUnits(TRouteBottomPanel)

const trouteActions = objectType => (
  objectType === 'trip'
    ? tripActions
    : routeActions
)

const trouteSelectors = objectType => (
  objectType === 'trip'
    ? tripSelectors
    : routeSelectors
)

const unitsOptions = intl => OrderedMap({
  imperial: fm(intl, commonUnits.imperial),
  metric: fm(intl, commonUnits.metric)
})

const parseQuerySegment = (query) => {
  if (!query?.highlight) {
    return null
  }
  const arr = query.highlight.split('-').map(n => parseInt(n, 10))
  if (arr.length === 2 && arr.every(n => isFinite(n))) {
    return arr
  }
  return null
}

// default route mapSettings
const RouteMapSettings = Record({
  showDistanceMarkers: false,
  showPOIs: true,
  showHeatLayer: false,
  showBikePaths: false,
  metricUnits: false,
  scrollWheelZoom: true,
  autoZoom: true
})

// default trip mapSettings
const TripMapSettings = trip => Record({
  showDistanceMarkers: false,
  showPOIs: true,
  showHeatLayer: false,
  showBikePaths: false,
  metricUnits: trip.metricUnits,
  scrollWheelZoom: true,
  autoZoom: true
})

const userPrefMappings = {
  showDistanceMarkers: 'route_viewer_enable_distance_markers',
  metricUnits: 'metric_units',
  scrollWheelZoom: 'route_viewer_enable_scrollwheel_zoom',
  autoZoom: 'trsp_auto_zoom',
  showPOIs: 'route_viewer_show_pois'
}

const getMapSettings = (user, troute, objectType) => {
  if (!troute) return
  const DefaultMapSettings = objectType === 'route'
    ? RouteMapSettings()
    : TripMapSettings(troute)()

  if (objectType === 'route') delete userPrefMappings.showPOIs // only respect user poi pref for trips

  const prefs = Object.entries(userPrefMappings)
    .map(([clientKey, serverKey]) => [clientKey, user?.preferences.get(serverKey, undefined)])
    .filter(([clientKey, value]) => value !== undefined)

  return DefaultMapSettings.merge(prefs)
}

// enums
export const SG_COLLAPSED = 0
export const SG_NORMAL = 1
export const SG_EXPANDED = 2

// CSS constants
export const SG_CONTROLS_HEIGHT = 32

const getSgHeight = (collapseState) => {
  switch (collapseState) {
    case SG_COLLAPSED:
      return SG_CONTROLS_HEIGHT
    case SG_EXPANDED:
      return 0 // 100%
    case SG_NORMAL:
    default:
      const MAX_HEIGHT = 280
      const MIN_HEIGHT = 120
      const proportional = window.innerHeight * 0.25
      return _.clamp(proportional, MIN_HEIGHT, MAX_HEIGHT)
  }
}

const adminMenuItemsForObjectType = (troute, objectType, toggleDebugWindow) => {
  return <>
    <AdminMenuItem
      menuKey='api_history'
      label='API History'
      href={`/users/${troute.userId}/api_history`} />
    <AdminMenuItem
      menuKey='log_in_view'
      label='Log in & View'
      onClick={() => fetchJSON(`/users/login_as?login_as%5Buser_id%5D=${troute.userId}`, null, { method: 'POST' })
        .then(() => { window.location.reload() })} />
    {objectType === 'route'
      ? <>
        <AdminMenuItem
          menuKey='log_in_edit'
          label='Log in & Edit'
          onClick={() => fetchJSON(`/users/login_as?login_as%5Buser_id%5D=${troute.userId}`, null, { method: 'POST' })
            .then(() => { window.location.href = `/routes/${troute.id}/edit` })} />
        <AdminMenuItem
          menuKey='show_route_info'
          label='Show route info'
          onClick={() => showModal(TROUTE_INFO_MODAL, { troute, objectType, objectId: troute.id })} />
      </>
      : <>
        <AdminMenuItem
          menuKey='clear_distances'
          label='Clear distances'
          onClick={() => fetchJSON(`/${objectType}s/${troute.id}/clear_distances`, null, { method: 'POST' }).then(_ => window.location.reload())} />
        <AdminMenuItem
          menuKey='clear_speeds'
          label='Clear speeds'
          onClick={() => fetchJSON(`/${objectType}s/${troute.id}/clear_speeds`, null, { method: 'POST' }).then(_ => window.location.reload())} />
        <AdminMenuItem
          menuKey='delete_photos'
          label='Delete all photos'
          onClick={() => showModal(TROUTE_DELETE_PHOTOS_MODAL, { objectType, troute })} />
      </>}
    <AdminMenuItem
      menuKey='replace_ele'
      label='Replace Elevation'
      onClick={() => fetchJSON(`/${objectType}s/${troute.id}/replace_elevations`, null, { method: 'POST' })
        .then(() => { window.location.reload() })} />
    <AdminMenuItem
      menuKey='delele'
      label='Delete'
      onClick={() => window.confirm('Are you sure?') && fetchJSON(`/${objectType}s/${troute.id}`, null, { method: 'DELETE' })
        .then(() => { window.location.reload() })} />
    <AdminMenuSeparator />
    <ExportSvgMenuItem />
    <AdminMenuItem
      menuKey='flag_as_important'
      label='Flag as important'
      onClick={() => showModal(FLAG_USER_AS_IMPORTANT_DIALOG, { userId: troute.userId })} />
    <AdminMenuItem
      menuKey='toggle_trsp_debug'
      label='Toggle map state inspector'
      onClick={toggleDebugWindow} />
  </>
}

class _TRouteShowPage extends React.PureComponent {
  constructor (props) {
    super(props)

    this.maybeRedirectToAmbassadorRoute()
    const { currentUser, troute, objectType, query } = props

    const highlightSlice = parseQuerySegment(query)
    const selectedFeature = highlightSlice && {
      type: featureTypes.SAMPLE_GRAPH_USER_SELECTION,
      data: {
        highlightSlice: [
          TrackLocation({ left: highlightSlice[0], percent: 0 }),
          TrackLocation({ left: highlightSlice[1], percent: 0 })
        ]
      }
    }

    const initialViewport = selectedFeature && troute
      ? Viewport.containingSlice(selectedFeature.data.highlightSlice, troute.trackPoints)
      : initialViewportForTRoute(troute)

    // _.every returns as soon as the predicate returns false, so unless
    // someone logs a huge trip all within a pz, this should be good
    const pzContained = _.every(troute?.rawPoints, p => 'ep' in p)
    this.state = {
      fullscreen: false,
      selectedFeature,
      hoveredFeature: null,
      initialTRoute: troute,
      hasUnsavedChanges: false,
      showDebugWindow: !!window.location.search.includes('DEBUG'),
      noticeDismissed: false,
      overlay: tileServices[troute?.defaultBasemap ?? 'rwgps'],
      initialViewport,
      // since we default to rwgps overlay on construction, padding only gets
      // passed into the MapboxMapEngine (this is to deal with highlight slices
      // in the url). if we ever allow for a different overlay on construction,
      // this value will need to be passed into/handled by GoogleMapEngine
      initialViewportPadding: selectedFeature && segmentPopoverClearance,
      zoom: initialViewport.zoom,
      mapZoomed: selectedFeature !== null,
      sgZoomed: selectedFeature !== null,
      mapSettings: getMapSettings(currentUser, troute, objectType),
      sgCollapseState: troute?.isStationary ? SG_EXPANDED : SG_NORMAL,
      pzContained
    }
  }

  maybeRedirectToAmbassadorRoute () {
    const { ambassadorRoute, query } = this.props
    if (!query.no_redir && ambassadorRoute) {
      window.location = `/ambassador_routes/${ambassadorRoute.id}`
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (prevState.selectedFeature !== this.state.selectedFeature) {
      this.handleSelectedFeatureChange(prevState.selectedFeature, this.state.selectedFeature)
    }
    const nextMetricUnits = this.props.currentUser.preferences.get('metric_units')
    if (prevProps.currentUser.preferences.get('metric_units') !== nextMetricUnits) {
      this.setState({ mapSettings: this.state.mapSettings.set('metricUnits', nextMetricUnits) })
    }
    if (prevProps.troute !== this.props.troute) {
      this.maybeRedirectToAmbassadorRoute()
    }
  }

  componentWillUnmount () {
    if (this.state.hasUnsavedChanges) {
      this.revertDirtyTRoute()
    }
  }

  // REMEMBER that either of these features could be null!
  handleSelectedFeatureChange = (prevFeature, currentFeature) => {
    const { location, history } = this.props
    const { autoZoom } = this.state.mapSettings

    // less typing = more better
    const g = _.get

    queryStringSet(location, 'highlight', currentFeature?.data.highlightSlice?.map(s => s.left).join('-'), history)

    // ignore switching from a feature that had a highlightSlice to a
    // SAMPLE_GRAPH_USER_SELECTION, it's just the user twiddling the sample graph
    if (g(prevFeature, 'data.highlightSlice') && g(currentFeature, 'type') === featureTypes.SAMPLE_GRAPH_USER_SELECTION) {
      return
    }

    // if we're going from a no-highlight-slice to yes-highlight-slice, zoom!
    if (!g(prevFeature, 'data.highlightSlice') && g(currentFeature, 'data.highlightSlice')) {
      const { highlightSlice } = currentFeature.data
      autoZoom && this.zoomToSliceBounds(highlightSlice, true)
      return
    }

    // if we're going from a yes-highlight-slice to yes-highlight-slice with a different firstI, zoom!
    if (g(prevFeature, 'data.highlightSlice') && g(currentFeature, 'data.highlightSlice') && (g(prevFeature, 'firstI') !== g(currentFeature, 'firstI'))) {
      const { highlightSlice } = currentFeature.data
      autoZoom && this.zoomToSliceBounds(highlightSlice, true)
      return
    }

    // if we're going from a yes-highlight-slice to no feature selected, reset zoom
    if (!currentFeature && g(prevFeature, 'data.highlightSlice')) {
      autoZoom && this.zoomToTRouteBounds()
      return // eslint-disable-line
    }
  }

  zoomToTRouteBounds = (alsoZoomGraph) => {
    const { troute } = this.props
    if (troute.isStationary) return
    const vp = Viewport({ bounds: troute.boundingBox })
    const hasSelectedFeature = _.get(this.state, 'selectedFeature.data.highlightSlice')

    this.setState(Object.assign({ mapZoomed: false }, alsoZoomGraph && { sgZoomed: false }))
    this._map._mapEngine.flyTo(vp, hasSelectedFeature && { padding: segmentPopoverClearance })
  }

  zoomToSliceBounds = (slice, alsoZoomGraph) => {
    const { troute } = this.props
    if (troute.isStationary) return
    const vp = Viewport.containingSlice(slice, troute.trackPoints)

    this.setState(Object.assign({ mapZoomed: true }, alsoZoomGraph && { sgZoomed: true }))
    this._map._mapEngine.flyTo(vp, { padding: segmentPopoverClearance })
  }

  toggleSGZoomed = () => {
    this.setState({ sgZoomed: !this.state.sgZoomed })
  }

  toggleDebugWindow = () => {
    this.setState({ showDebugWindow: !this.state.showDebugWindow })
  }

  onFeatureSelected = (selectedFeature, zoomOnHighlightSliceChange) => {
    // hoveredFeature always gets cleared when the selected feature is changed or updated
    this.setState({ selectedFeature, hoveredFeature: null, sgZoomed: zoomOnHighlightSliceChange })
  }

  onFeatureHovered = (hoveredFeature) => {
    this.setState({ hoveredFeature })
  }

  clearSelectedFeature = () => {
    this.setState({ selectedFeature: null })
  }

  clearHoveredFeature = () => {
    this.setState({ hoveredFeature: null })
  }

  renderSelectedFeature = renderSelectedFeature.bind(this)

  renderHoveredFeature = renderHoveredFeature.bind(this)

  onMapSnap = ({ snapPoint, snapIndex }) => {
    this.onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: { highlightPoint: { trackPoint: snapPoint, index: snapIndex } }
    })
  }

  renderDebugWindow = () => {
    const style = {
      position: 'fixed',
      padding: 20,
      right: 20,
      top: 130,
      zIndex: 9999,
      maxHeight: window.innerHeight - 360 + 'px',
      maxWidth: '350px',
      overflow: 'auto',
      background: '#FFF',
      fontSize: '13px'
    }

    // feel free to add/remove things from this as needed when you
    // are debugging features, just remember to reset it to just
    // selectedFeature and hoveredFeature when you go to make a PR.
    //
    // PROTIP: you can make the map inspector persist through reloads
    // by adding &DEBUG to the URL params
    const itemsToInspect = [
      ['selectedFeature', this.state.selectedFeature],
      ['hoveredFeature', this.state.hoveredFeature]
    ]

    return ReactDOM.createPortal(<div style={style}>
      {itemsToInspect.map(([label, data]) => <Fragment key={label}>
        <strong>{label}</strong>
        <pre>{JSON.stringify(data, null, '  ')}</pre>
      </Fragment>)}
    </div>, document.body)
  }

  onUnitsChange = (selectedUnits) => {
    const { currentUser, setPreference } = this.props
    const metricUnits = selectedUnits === 'metric'
    if (currentUser.id) {
      setPreference(userPrefMappings['metricUnits'], metricUnits)
    }
    this.setState({ mapSettings: this.state.mapSettings.set('metricUnits', metricUnits) })
  }

  onDistanceMarkersChange = (showDistanceMarkers) => {
    const { currentUser, setPreference } = this.props
    if (currentUser.id) {
      setPreference(userPrefMappings['showDistanceMarkers'], showDistanceMarkers)
    }
    this.setState({ mapSettings: this.state.mapSettings.set('showDistanceMarkers', showDistanceMarkers) })
  }

  onPOIsChange = showPOIs => {
    const { currentUser, setPreference } = this.props
    if (currentUser.id) {
      setPreference(userPrefMappings['showPOIs'], showPOIs)
    }
    this.setState({ mapSettings: this.state.mapSettings.set('showPOIs', showPOIs) })
  }

  onShowHeatLayerChange = showHeatLayer => {
    this.setState({ mapSettings: this.state.mapSettings.set('showHeatLayer', showHeatLayer) })
  }

  onShowBikePathsChange = showBikePaths => {
    this.setState({ mapSettings: this.state.mapSettings.set('showBikePaths', showBikePaths) })
  }

  onScrollWheelZoomChange = (scrollWheelZoom) => {
    const { currentUser, setPreference } = this.props
    if (currentUser.id) {
      setPreference(userPrefMappings['scrollWheelZoom'], scrollWheelZoom)
    }
    this.setState({ mapSettings: this.state.mapSettings.set('scrollWheelZoom', scrollWheelZoom) })
  }

  onAutoZoomChange = (autoZoom) => {
    const { currentUser, setPreference } = this.props
    if (currentUser.id) {
      setPreference(userPrefMappings['autoZoom'], autoZoom)
    }
    this.setState({ mapSettings: this.state.mapSettings.set('autoZoom', autoZoom) })
  }

  toggleFullscreen = () => {
    this.setState(
      { fullscreen: !this.state.fullscreen },
      () => this._map._mapEngine.resizeToParentHeight()
    )
  }

  revertUnsavedChanges = () => this.setState({ hasUnsavedChanges: false })

  dirtyUpdateTRoute = (record) => {
    this.props.dirtyUpdateTRoute(record)
    this.setState({ hasUnsavedChanges: true })
  }

  revertDirtyTRoute = () => {
    const { dirtyUpdateTRoute } = this.props
    const { initialTRoute } = this.state
    dirtyUpdateTRoute(initialTRoute)
    this.setState({ hasUnsavedChanges: false })
  }

  onViewportChanged = (newViewport) => {
    this.setState({ zoom: newViewport.zoom })
  }

  expandSg = () => {
    this.setState({ sgCollapseState: Math.min(this.state.sgCollapseState + 1, 2) })
  }

  collapseSg = () => {
    this.setState({ sgCollapseState: Math.max(this.state.sgCollapseState - 1, 0) })
  }

  render () {
    const {
      currentUser,
      objectType,
      location,
      onTriggerFileBrowser,
      pendingPhotoUploads,
      troute,
      troutePhotos,
      troutePOIs,
      removePhoto,
      updateTRoute,
      intl
    } = this.props

    const {
      selectedFeature,
      hoveredFeature,
      showDebugWindow,
      overlay,
      fullscreen,
      initialViewport,
      initialViewportPadding,
      zoom,
      sgZoomed,
      mapSettings,
      sgCollapseState,
      pzContained
    } = this.state

    if (!troute) {
      // This situation can occur momentarily on route deletion, after the route
      // has been deleted but before browserHistory.replace('/') call takes effect.
      // We need to handle it nicely or the error will trigger our errorbound UI
      return null
    }

    const getPoiPhotos = (poi) => {
      return poi.photoIds?.map(pid => ({ 'id': pid }))
    }

    const noPoints = troute.trackPoints.length === 0
    let sgHeight = noPoints
      ? SG_CONTROLS_HEIGHT
      : getSgHeight(sgCollapseState)
    if (sgHeight === 0) sgHeight = '100%'
    const mapHeight = sgHeight === '100%'
      ? 0
      : `calc(100% - ${sgHeight}px)`

    return (
      <MetricUnitsContext.Provider value={mapSettings.metricUnits}>
        <ThreePanelLayout fullscreen={fullscreen}>
          <TRouteSidePanel
            mapRef={this._map}
            currentUser={currentUser}
            objectType={objectType}
            onTriggerFileBrowser={onTriggerFileBrowser}
            pendingPhotoUploads={pendingPhotoUploads}
            troute={troute}
            troutePhotos={troutePhotos}
            troutePOIs={troutePOIs}
            onPhotoDeleted={removePhoto}
            selectedFeature={selectedFeature}
            onFeatureHovered={this.onFeatureHovered}
            onFeatureSelected={this.onFeatureSelected} />
          <MainPanel height={mapHeight}>
            <Topo style={{ height: '100%', width: '100%' }}>
              {pzContained || noPoints
                ? <EmptyPlaceholder pzContained={pzContained} noPoints={noPoints} />
                : <>
                  {/* This is a little odd-looking, but the AdminMenuItem component
                  needs to go inside one of the *Panel components or else it won't
                  mount */}
                  {adminMenuItemsForObjectType(troute, objectType, this.toggleDebugWindow)}
                  {showDebugWindow && this.renderDebugWindow()}
                  {!troute.isStationary && <MapV2
                    scrollWheelZoom={mapSettings.scrollWheelZoom}
                    showHeatLayer={mapSettings.showHeatLayer}
                    showBikePaths={mapSettings.showBikePaths}
                    overlay={overlay}
                    onChangeOverlay={overlay => this.setState({ overlay })}
                    ref={r => { this._map = r }}
                    onViewportChanged={this.onViewportChanged}
                    initialViewport={initialViewport}
                    initialViewportPadding={initialViewportPadding}
                    controls={<FullscreenButton intl={intl} isFullscreen={fullscreen} onClick={this.toggleFullscreen} />}>
                    <SettingsMenu
                      intl={intl}
                      overlayType={overlay.type}
                      mapSettings={mapSettings}
                      onUnitsChange={this.onUnitsChange}
                      onDistanceMarkersChange={this.onDistanceMarkersChange}
                      onPOIsChange={this.onPOIsChange}
                      onShowHeatLayerChange={this.onShowHeatLayerChange}
                      onShowBikePathsChange={this.onShowBikePathsChange}
                      onScrollWheelZoomChange={this.onScrollWheelZoomChange}
                      onAutoZoomChange={this.onAutoZoomChange}
                      unitsOptions={unitsOptions(intl)} />
                    <TrackLine trackPoints={troute.trackPoints} showDistanceMarkers={mapSettings.showDistanceMarkers} />
                    <TrackSnapPoint hoveredFeature={hoveredFeature} troute={troute} onSnap={this.onMapSnap} onUnSnap={this.clearHoveredFeature} />
                    {/* TODO refactor troutePhotos to accept photos as a separate prop */}
                    {troutePhotos && <PhotoClusters troute={troute.set('photos', troutePhotos)} clusterStrategy={objectType} zoom={zoom} />}
                    {mapSettings.showPOIs && troutePOIs && troutePOIs.map(poi => <POIv3 key={poi.id} poi={poi} photos={troutePhotos.concat(getPoiPhotos(poi))} />)}
                    {this.renderSelectedFeature()}
                    {this.renderHoveredFeature()}
                  </MapV2>}
                </>}
            </Topo>
          </MainPanel>
          <BottomPanel height={sgHeight}>
            <MetricAwareTRouteBottomPanel
              troute={troute}
              height={sgHeight}
              updateDirtyTRoute={this.dirtyUpdateTRoute}
              revertDirtyTRoute={this.revertDirtyTRoute}
              key={'' + fullscreen}
              objectType={objectType}
              location={location}
              currentUser={currentUser}
              updateTRoute={updateTRoute}
              revertUnsavedChanges={this.revertUnsavedChanges}
              selectedFeature={selectedFeature}
              hoveredFeature={hoveredFeature}
              sgZoomed={sgZoomed}
              onZoomClicked={this.toggleSGZoomed}
              onFeatureSelected={this.onFeatureSelected}
              onFeatureHovered={this.onFeatureHovered}
              collapseState={sgCollapseState}
              onExpand={this.expandSg}
              onCollapse={this.collapseSg}
            />
          </BottomPanel>
        </ThreePanelLayout>
      </MetricUnitsContext.Provider>
    )
  }
}

class DroppableTRouteShowPage extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      pendingUploads: 0
    }
  }

  componentDidMount () {
    // recording the recentActivity here because the TRouteShowPage component
    // gets double mounted based on userIsOwner below
    const { trouteId, objectType, currentUser } = this.props
    const capitalizedObjectType = `${objectType[0].toUpperCase()}${objectType.slice(1)}`
    trouteId && currentUser.id && apiv3.users.recordActivity('viewed', capitalizedObjectType, trouteId)
    this.showShareDialogIfShareInURL()
    this.showGalleryIfPhotoInURL()
  }

  showShareDialogIfShareInURL = () => {
    const { troute, objectType, query } = this.props
    if (troute && query.openShare === 'true') {
      showModal(SHARE_MODAL, {
        objectType,
        objectId: troute.id,
        objectTitle: troute.name,
        objectUrl: urls[`${objectType}s`].show(troute.id, troute.privacyCode),
        privacyCode: troute.privacyCode
      })
    }
  }

  // copied from rsp
  // This isn't exactly elegant, but it's very functional and allows us to
  // easily support the old fragment-based url scheme for photos without
  // upgrading react-router
  showGalleryIfPhotoInURL () {
    const galleryIsOpen = document.getElementById('#react-gallery') // again, I never said it was elegant
    if (this._didShowGallery || galleryIsOpen) { return } // use a flag to prevent weird state clobbering

    const { troute, objectType, troutePhotos, currentUser, removePhoto } = this.props
    if (troute && window.location.hash.match(/#photos\/\d+$/)) {
      this._didShowGallery = true
      const userIsOwner = troute.userId === currentUser.id
      const activePhotoId = parseInt(window.location.hash.match(/#photos\/(\d+)$/)[1])
      const orderedPhotos = _.orderBy(troutePhotos, 'id', 'desc')
      showGallery({
        photos: orderedPhotos,
        activePhotoId,
        readonly: !userIsOwner,
        parentType: objectType,
        parentId: troute.id,
        onPhotoDeleted: removePhoto
      })
    }
  }

  onDrop = (files) => {
    const { addPhoto } = this.props
    this.setState((prevState) => ({ pendingUploads: prevState.pendingUploads + files.length }))
    let index = 0
    const nextUpload = () => {
      if (!files[index]) { return null }
      const nextUpload = files[index]
      index++

      return addPhoto(nextUpload)
        .then(() => this.setState((prevState) => ({ pendingUploads: prevState.pendingUploads - 1 })))
    }
    const pool = new PromisePool(nextUpload, getPhotoUploadMaxConcurrency())

    pool.start()
  }

  onTriggerFileBrowser = () => {
    this._dropzone && this._dropzone.open()
  }

  render () {
    const { currentUser, troute } = this.props
    const { pendingUploads } = this.state
    const userIsOwner = troute && (currentUser.id === troute.userId)
    return userIsOwner
      ? <DroppablePage
        ref={r => { this._dropzone = r }}
        multiple
        onDrop={this.onDrop}>
        <_TRouteShowPage {...this.props} onTriggerFileBrowser={this.onTriggerFileBrowser} pendingPhotoUploads={pendingUploads} />
      </DroppablePage>
      : <_TRouteShowPage {...this.props} />
  }
}

class LoadableTRouteShowPage extends React.PureComponent {
  render () {
    const { objectType, troute, loadTRoute } = this.props
    return <Loadable
      resourceLoaded={(!!troute && !!troute.metrics) || loadTRoute}
      resourceId={`${objectType}${troute?.id}`}
      component={DroppableTRouteShowPage}
      {...this.props} />
  }
}

const mapStateToProps = (state, ownProps) => {
  const troute = trouteSelectors(ownProps.objectType).find(state, ownProps.trouteId)
  return {
    currentUser: state.currentUser,
    troute,
    ambassadorRoute: troute && ambassadorRouteSelectors.byRouteId(state, troute.id),
    troutePhotos: troute && photoSelectors.findByIds(state, troute.photoIds),
    troutePOIs: troute && pointOfInterestSelectors.findByIds(state, troute.poiIds)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { objectType, trouteId, privacyCode } = ownProps

  const objectActions = trouteActions(objectType)

  return {
    addPhoto: (photo) => {
      const body = new window.FormData()
      body.append('file', photo)
      body.append('parent_type', objectType)
      body.append('parent_id', trouteId)

      return dispatch(photoActions.create(body))
        .then(r => dispatch(objectActions.photoAdded(trouteId, r.action.payload.photo.id)))
    },
    removePhoto: ({ parentId, photoId }) => dispatch(trouteActions(objectType).photoRemoved(parentId, photoId)),
    loadTRoute: () => dispatch(
      objectActions.read(trouteId, { privacyCode, count: true })
    ),
    dirtyUpdateTRoute: record => dispatch(objectActions.dirtyUpdate(record)),
    // a trackpoints-only update to not clobber poi
    // NOTE: this won't work to update any other troute attributes!!
    updateTRoute: (record) => dispatch(objectActions.update(record.id, { [objectType]: { trackPoints: JSON.stringify(record.rawPoints) } })),
    setPreference: (preference, value) => dispatch(userActions.setPreference(preference, value))
  }
}

const ConnectedTRouteShowPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(LoadableTRouteShowPage))

const parseObjectType = pathname => {
  if (pathname.match(/^\/routes\/\w+/i)) {
    return 'route'
  }
  if (pathname.match(/^\/trips\/\w+/i)) {
    return 'trip'
  }
  softError('Unable to determine object type for TRouteShowPage, defaulting to route')
  return 'route'
}

// and at the veeeeery top, we want to parse the trouteId into an int:
export const TRouteShowPage = ({ match, ...props }) => {
  const query = queryString.parse(props.location.search)
  return <ConnectedTRouteShowPage
    {...props}
    query={query}
    objectType={parseObjectType(props.location.pathname)}
    trouteId={parseInt(match.params.trouteId, 10)}
    privacyCode={query?.privacy_code}
  />
}
